import type { ReactNode } from 'react';
import { Skeleton } from '../Skeleton/Skeleton.tsx';
import { Sparkline } from '../Sparkline/Sparkline.tsx';
import styles from './KpiTile.module.css';

type KpiDeltaDirection = 'up' | 'down' | 'flat';

export type KpiDeltaTone = 'good' | 'bad' | 'neutral';

export type KpiDelta = { value: string; direction: KpiDeltaDirection; label: string; tone?: KpiDeltaTone };

const TONE_OF_DIRECTION: Readonly<Record<KpiDeltaDirection, KpiDeltaTone>> = {
  up: 'good',
  down: 'bad',
  flat: 'neutral',
};

export type KpiDetail = { readonly label: string; readonly value: string };

export type KpiTileProps = {
  label: string;
  value?: ReactNode;
  delta?: KpiDelta | undefined;
  details?: readonly KpiDetail[] | undefined;
  loading?: boolean;
  emphasis?: KpiEmphasis;
  tone?: KpiTone | undefined;
  hint?: string | undefined;
  action?: ReactNode;
  icon?: KpiIcon | undefined;
  trend?: readonly number[] | undefined;
};

export type KpiIconTone = 'neutral' | 'blue' | 'amber' | 'indigo' | 'red' | 'green';

export type KpiIcon = { readonly glyph: ReactNode; readonly tone: KpiIconTone };

export type KpiTone = 'warning' | 'danger';

export type KpiEmphasis = 'standard' | 'hero';

const NO_VALUE = '—';

const DELTA_SYMBOL = { up: '▲', down: '▼', flat: '•' } as const;

export const KpiTile = ({
  label,
  value,
  delta,
  details = [],
  loading = false,
  emphasis = 'standard',
  tone,
  hint,
  action,
  icon,
  trend,
}: KpiTileProps) => (
  <div
    className={[styles['tile'], styles[emphasis]].join(' ')}
    data-tone={tone}
    data-with-icon={icon === undefined ? undefined : true}
  >
    {icon === undefined ? null : (
      <span className={styles['icon']} data-icon-tone={icon.tone} aria-hidden="true">
        {icon.glyph}
      </span>
    )}
    <span className={styles['label']}>{label}</span>
    {loading ? (
      <Skeleton height="32px" width="60%" />
    ) : (
      <span className={styles['value']}>{value ?? NO_VALUE}</span>
    )}
    {trend === undefined || loading ? null : (
      <span className={styles['trend']}>
        <Sparkline values={trend} />
      </span>
    )}
    {delta && !loading ? (
      <span
        className={[styles['delta'], styles[delta.tone ?? TONE_OF_DIRECTION[delta.direction]]].join(' ')}
        aria-label={delta.label}
      >
        {DELTA_SYMBOL[delta.direction]} {delta.value}
      </span>
    ) : null}
    {hint === undefined || loading ? null : <span className={styles['hint']}>{hint}</span>}
    {action === undefined || loading ? null : <span className={styles['action']}>{action}</span>}
    {details.length === 0 || loading ? null : (
      <dl className={styles['details']}>
        {details.map((detail) => (
          <div key={detail.label}>
            <dt>{detail.label}</dt>
            <dd>{detail.value}</dd>
          </div>
        ))}
      </dl>
    )}
  </div>
);

export type KpiGridProps = { children: ReactNode; columns?: 3 | 4 };

export const KpiGrid = ({ children, columns = 3 }: KpiGridProps) => (
  <div className={styles['grid']} data-columns={columns}>
    {children}
  </div>
);
